/**
 * EditMemberRolesDialog — cambia los roles de alguien que ya está en el
 * proyecto (OT-0058 §4.3).
 *
 * Es el caso que el equipo con un rol por persona no resolvía: quien ya
 * participa (p. ej. el consultor de preventa) también es quien aprueba como
 * gerente comercial. Se le agrega el rol aquí, sin sacarlo del equipo.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '../../../api/client'
import { updateProjectMember, type ProjectMember } from '../../../api/project_members'
import { useAccessToken } from '../../../auth/useAccessToken'
import { pushToast } from '../../../stores/toastStore'
import { Dialog } from '../../Dialog'
import { RoleChecklist } from './RoleChecklist'
import type { RoleOption } from './roles'

interface EditMemberRolesDialogProps {
  /** El miembro a editar; el diálogo se monta al abrir (`key` por miembro). */
  member: ProjectMember
  memberName: string
  projectId: string
  /** Roles de la sección del miembro (internos o del cliente). */
  roles: RoleOption[]
  onClose: () => void
}

export function EditMemberRolesDialog({
  member,
  memberName,
  projectId,
  roles,
  onClose,
}: EditMemberRolesDialogProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string[]>(member.roles)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return updateProjectMember(accessToken, projectId, member.id, { roles: selected })
    },
    onSuccess: () => {
      pushToast({ kind: 'success', message: t('team.toasts.updated') })
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'members'] })
      onClose()
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('team.toasts.update_failed')
      setError(
        code ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback }) : fallback,
      )
    },
  })

  const unchanged =
    selected.length === member.roles.length && selected.every((r) => member.roles.includes(r))

  return (
    <Dialog
      open
      onClose={() => {
        if (!mutation.isPending) onClose()
      }}
      title={t('team.roles.edit_title', { name: memberName })}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('team.remove_dialog.cancel')}
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || selected.length === 0 || unchanged}
            className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? t('team.roles.saving') : t('team.roles.save')}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="font-body text-sm text-ink-muted">{t('team.roles.edit_help')}</p>
        <RoleChecklist
          roles={roles}
          value={selected}
          onChange={setSelected}
          disabled={mutation.isPending}
          ariaLabel={t('team.add_internal_dialog.role_label')}
        />
        {selected.length === 0 ? (
          <p className="text-[11px] text-critical">{t('team.roles.at_least_one')}</p>
        ) : null}
        {error ? (
          <div className="rounded-md border border-critical/40 bg-critical-soft p-2.5 font-mono text-xs text-critical">
            {error}
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}
